import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CompareArrows as CompareIcon } from '@mui/icons-material';

export type ReanalysisChoice = 'old' | 'new';

export interface ReanalysisResultRow {
  element: string;
  [key: string]: any;
}

/**
 * Snapshot shape stored by the backend on `metadonnees.reclamation_previous_analysis`
 * when a reclamation is accepted and the lot is reopened for re-analysis.
 */
export interface ReclamationPreviousAnalysis {
  archived_at?: string;
  reclamation_id?: string;
  certificate_number?: string | null;
  target_lab?: string | null;
  analysis_results?: ReanalysisResultRow[];
  per_lab?: Record<string, any>;
}

/** One sample's old (v1) vs new (re-analysed) readings. */
export interface ReanalysisSampleInput {
  id: string;
  sample_id: string;
  external_ref?: string | null;
  previousResults: ReanalysisResultRow[];
  newResults: ReanalysisResultRow[];
}

/** Default value accessor: shows the most meaningful numeric field available. */
export function defaultDisplayValue(r: ReanalysisResultRow | undefined): string {
  if (!r) return '—';
  const v =
    r.concentration_final_percent ??
    r.value ??
    r.concentration_ppm ??
    r.result ??
    null;
  if (v === null || v === undefined || v === '') return '—';
  const num = Number(v);
  if (!Number.isFinite(num)) return String(v);
  return Math.abs(num) < 1 ? num.toPrecision(3) : num.toLocaleString('fr-FR', { maximumFractionDigits: 4 });
}

/** Numeric value used to detect whether old and new differ. */
function numericValue(r: ReanalysisResultRow | undefined): number | null {
  if (!r) return null;
  const v =
    r.concentration_final_percent ??
    r.value ??
    r.concentration_ppm ??
    r.result ??
    null;
  const num = Number(v);
  return Number.isFinite(num) ? num : null;
}

export interface ReanalysisComparisonPanelProps {
  /** Every reopened sample in the lot. The panel renders ONE table for them all. */
  samples: ReanalysisSampleInput[];
  /** selections[sampleId][element] = 'old' | 'new'. Missing element defaults to 'new'. */
  selections: Record<string, Record<string, ReanalysisChoice>>;
  onSelectionsChange: (next: Record<string, Record<string, ReanalysisChoice>>) => void;
  certificateNumber?: string | null;
  getDisplayValue?: (r: ReanalysisResultRow | undefined) => string;
}

/**
 * Lot-level old↔new comparison for a re-analysed reclamation.
 *
 * Mirrors the 4-Acides ↔ Fusion matrix: ONE table for the whole lot, with one
 * row per sample and, for every element, two side-by-side columns — "Ancienne
 * (v1)" and "Nouvelle". The supervisor clicks a cell to choose the source for
 * that sample×element (green = chosen). Validation stays a single lot-level
 * action; the chosen values feed the re-issued (v2) certificate.
 */
const ReanalysisComparisonPanel: React.FC<ReanalysisComparisonPanelProps> = ({
  samples,
  selections,
  onSelectionsChange,
  certificateNumber,
  getDisplayValue = defaultDisplayValue,
}) => {
  // Per-sample lookups: element -> row, for old and new.
  const sampleMaps = useMemo(() => {
    return samples.map((s) => {
      const oldByEl = new Map<string, ReanalysisResultRow>();
      (s.previousResults || []).forEach((r) => r?.element && oldByEl.set(r.element, r));
      const newByEl = new Map<string, ReanalysisResultRow>();
      (s.newResults || []).forEach((r) => r?.element && newByEl.set(r.element, r));
      return { sample: s, oldByEl, newByEl };
    });
  }, [samples]);

  // Union of every element seen across the lot (old or new), sorted.
  const elements = useMemo(() => {
    const set = new Set<string>();
    sampleMaps.forEach(({ oldByEl, newByEl }) => {
      oldByEl.forEach((_v, k) => set.add(k));
      newByEl.forEach((_v, k) => set.add(k));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
  }, [sampleMaps]);

  const choiceFor = (sampleId: string, el: string): ReanalysisChoice =>
    selections[sampleId]?.[el] || 'new';

  const setOne = (sampleId: string, el: string, choice: ReanalysisChoice) => {
    onSelectionsChange({
      ...selections,
      [sampleId]: { ...(selections[sampleId] || {}), [el]: choice },
    });
  };

  const setAll = (choice: ReanalysisChoice) => {
    const next: Record<string, Record<string, ReanalysisChoice>> = {};
    sampleMaps.forEach(({ sample, oldByEl, newByEl }) => {
      const perEl: Record<string, ReanalysisChoice> = {};
      const els = new Set<string>([...oldByEl.keys(), ...newByEl.keys()]);
      els.forEach((el) => (perEl[el] = choice));
      next[sample.id] = perEl;
    });
    onSelectionsChange(next);
  };

  const changedCount = useMemo(() => {
    let n = 0;
    sampleMaps.forEach(({ oldByEl, newByEl }) => {
      const els = new Set<string>([...oldByEl.keys(), ...newByEl.keys()]);
      els.forEach((el) => {
        const o = numericValue(oldByEl.get(el));
        const nw = numericValue(newByEl.get(el));
        if (o !== null && nw !== null && o !== nw) n += 1;
      });
    });
    return n;
  }, [sampleMaps]);

  if (samples.length === 0 || elements.length === 0) return null;

  const cellSx = (chosen: boolean, missing: boolean) => ({
    cursor: missing ? 'default' : 'pointer',
    textAlign: 'center' as const,
    fontWeight: chosen ? 700 : 400,
    backgroundColor: missing ? '#f5f5f5' : chosen ? '#a5d6a7' : 'inherit',
    color: missing ? 'text.disabled' : chosen ? '#1b5e20' : 'text.secondary',
    fontSize: '0.72rem',
    py: 0.4,
    px: 0.8,
    userSelect: 'none' as const,
    '&:hover': missing ? {} : { backgroundColor: chosen ? '#81c784' : '#e8f5e9' },
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: 'warning.main', borderWidth: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }} flexWrap="wrap">
        <CompareIcon color="warning" />
        <Typography variant="subtitle1" fontWeight={700}>
          Réanalyse du lot — comparaison v1 ↔ nouvelle
        </Typography>
        <Chip size="small" label={`${samples.length} échantillon(s)`} />
        {changedCount > 0 && (
          <Chip size="small" color="warning" label={`${changedCount} valeur(s) modifiée(s)`} />
        )}
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Ce lot a été renvoyé suite à une réclamation
        {certificateNumber ? ` sur le certificat ${certificateNumber}` : ''}. Pour chaque élément,
        cliquez sur la valeur à conserver (<strong>v1</strong> = ancienne, <strong>Nouv.</strong> =
        nouvelle). La cellule choisie apparaît en vert. Par défaut, les nouvelles valeurs sont
        sélectionnées. La validation reste au niveau du lot entier.
      </Alert>

      <Stack direction="row" gap={1} sx={{ mb: 1.5 }} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={() => setAll('new')}>
          Tout : nouvelles valeurs
        </Button>
        <Button size="small" variant="outlined" color="inherit" onClick={() => setAll('old')}>
          Tout : anciennes valeurs (v1)
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ maxHeight: 560, overflowX: 'auto' }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            borderCollapse: 'separate',
            '& .MuiTableCell-root': { borderRight: '1px solid #eceff1' },
          }}
        >
          <TableHead>
            {/* HEADER ROW 1: element name spanning its 2 sub-columns */}
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontWeight: 'bold',
                  minWidth: 170,
                }}
              >
                Échantillon
              </TableCell>
              {elements.map((el) => (
                <TableCell
                  key={`hdr1-${el}`}
                  colSpan={2}
                  sx={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    backgroundColor: '#bbdefb',
                    color: '#0d47a1',
                    fontSize: '0.72rem',
                    borderRight: '2px solid #90a4ae',
                  }}
                >
                  {el}
                </TableCell>
              ))}
            </TableRow>
            {/* HEADER ROW 2: v1 / Nouv. sub-labels */}
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: '#42a5f5',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.68rem',
                }}
              >
                Réf.
              </TableCell>
              {elements.map((el) => (
                <React.Fragment key={`hdr2-${el}`}>
                  <TableCell sx={{ backgroundColor: '#eceff1', textAlign: 'center', fontWeight: 'bold', color: '#546e7a', fontSize: '0.62rem', py: 0.2 }}>
                    v1
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#e3f2fd', textAlign: 'center', fontWeight: 'bold', color: '#1565c0', fontSize: '0.62rem', py: 0.2, borderRight: '2px solid #90a4ae' }}>
                    Nouv.
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sampleMaps.map(({ sample, oldByEl, newByEl }) => (
              <TableRow key={sample.id} hover>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    backgroundColor: '#f5f7fa',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                  }}
                >
                  {sample.sample_id}
                  {sample.external_ref ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {sample.external_ref}
                    </Typography>
                  ) : null}
                </TableCell>
                {elements.map((el) => {
                  const oldRow = oldByEl.get(el);
                  const newRow = newByEl.get(el);
                  const oldMissing = oldRow === undefined;
                  const newMissing = newRow === undefined;
                  const o = numericValue(oldRow);
                  const nw = numericValue(newRow);
                  const changed = o !== null && nw !== null && o !== nw;
                  const choice = choiceFor(sample.id, el);
                  return (
                    <React.Fragment key={`${sample.id}-${el}`}>
                      <Tooltip title={oldMissing ? 'Absente en v1' : changed ? 'Ancienne valeur (v1) — cliquer pour choisir' : 'Ancienne valeur (v1)'} arrow>
                        <TableCell
                          sx={cellSx(choice === 'old' && !oldMissing, oldMissing)}
                          onClick={() => { if (!oldMissing) setOne(sample.id, el, 'old'); }}
                        >
                          {getDisplayValue(oldRow)}
                        </TableCell>
                      </Tooltip>
                      <Tooltip title={newMissing ? 'Absente dans la nouvelle analyse' : changed ? 'Nouvelle valeur — cliquer pour choisir' : 'Nouvelle valeur'} arrow>
                        <TableCell
                          sx={{
                            ...cellSx(choice === 'new' && !newMissing, newMissing),
                            borderRight: '2px solid #90a4ae',
                            ...(changed && choice !== 'new' ? { outline: '2px solid #ffb74d', outlineOffset: '-2px' } : {}),
                          }}
                          onClick={() => { if (!newMissing) setOne(sample.id, el, 'new'); }}
                        >
                          {getDisplayValue(newRow)}
                        </TableCell>
                      </Tooltip>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

/**
 * Build the final results array for ONE sample from the supervisor's per-element
 * choice. For each element, picks the row from the chosen source (defaulting to
 * new). Elements present only in one source are always included.
 */
export function applyReanalysisSelection(
  previousResults: ReanalysisResultRow[],
  newResults: ReanalysisResultRow[],
  selection: Record<string, ReanalysisChoice>,
): ReanalysisResultRow[] {
  const oldByEl = new Map<string, ReanalysisResultRow>();
  (previousResults || []).forEach((r) => r?.element && oldByEl.set(r.element, r));
  const newByEl = new Map<string, ReanalysisResultRow>();
  (newResults || []).forEach((r) => r?.element && newByEl.set(r.element, r));

  const elements = new Set<string>([...oldByEl.keys(), ...newByEl.keys()]);
  const out: ReanalysisResultRow[] = [];
  elements.forEach((el) => {
    const choice = selection[el] || 'new';
    const chosen = choice === 'old' ? oldByEl.get(el) : newByEl.get(el);
    // Fallback to the other source if the chosen one is missing for this element.
    const row = chosen || newByEl.get(el) || oldByEl.get(el);
    if (row) out.push(row);
  });
  return out;
}

export default ReanalysisComparisonPanel;
